import { pageTitle } from '@palaccount/config';
import {
  SETTING_GROUPS,
  accountCodeSetting,
  isSettingKey,
  type SettingGroup,
  type SettingRow,
  type SettingValue,
} from '@palaccount/contracts';
import { Eye, Lock, LockOpen, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { i18n } from '@/i18n';
import { holds, useCapabilities } from '@/lib/permissions';
import { localizeServerMessage } from '@/lib/server-messages';

import { useAccounts } from '@/features/accounts/api';

import {
  useLockSetting,
  useOrgSetting,
  useOverrideSetting,
  useSecurityRoles,
  useSettings,
  useUpdateSetting,
} from './api';
import {
  VIEW_AS_OFF,
  decodeViewAs,
  encodeViewAs,
  previewAuthority,
  previewRows,
  type ViewAsTarget,
} from './viewing-as';

/**
 * Is this row's value an account code, and may it be blank?
 *
 * `SettingRow.key` crosses the wire as a plain string, so it is narrowed here
 * rather than asserted — an unknown key means "not an account row", which is
 * the safe answer and also the true one for a client running against a newer
 * server.
 */
function accountMode(key: string): 'required' | 'optional' | null {
  return isSettingKey(key) ? accountCodeSetting(key) : null;
}

/**
 * ADM-01 — General Settings (PART 15.6): nine groups in a rail, every row
 * with its name, hint, scope badge and control; the padlock on org/role rows;
 * owner-only rows arrive only for the Owner (the API never sends them to
 * anybody else — BR-ADM-01-02). Rows the caller may not change are dimmed and
 * inert WITH their value still visible (BR-ADM-01-07 — which is GEN-165's
 * greyed-field rule worn by this screen).
 *
 * DEVIATION, recorded — AND NOW AUTHORISED. The spec's screen applies each
 * change at once with no Save button (BR-ADM-01-13); this build batches
 * changes per group behind a Save-group button (the Phase-4 instruction),
 * which also satisfies BR-ADM-06-03's instinct — nothing is silently
 * discarded, the dirty state is visible until saved or reset.
 *
 * ── THE AUTHORITY, WHICH THIS NOTE USED TO LACK ────────────────────────────
 *
 * "DEVIATION, recorded" was true and incomplete: recording a deviation is not
 * being permitted one. The PART 21 sweep (27 Aug 2026) found this argued in
 * code and authorised by no decision document, alongside the PDF-export
 * deviation in `packages/contracts/src/data-transfer.ts`. Both are now ruled.
 *
 * `DELEGATED RULINGS — 26 Aug 2026.md`, the "Ratified in passing (27 Aug)"
 * block: *"the ADM-01/ADM-06 Save button deviation (against unretired
 * BR-ADM-01-13) and the PDF-export deviation (against ADM-03 §5) — both
 * argued in code, now authorised; the implementing wave adds the fixed-opening
 * notes."*
 *
 * READ THE PARENTHESIS. **BR-ADM-01-13 IS NOT RETIRED**, and the ruling says
 * so in its own sentence. What is authorised is the DEVIATION; the rule keeps
 * standing, which is why nothing here claims otherwise and why no retirement
 * notice is proposed for its spec row. The project's fixed opening — "Retired
 * by delegated ruling" — is the form a notice takes on a row the spec is
 * giving up (D34's POS receipt buttons are the worked example, at
 * `pos-page.tsx`). Writing it beside a rule that still binds would buy the
 * look of authority at the price of the record being true, which is the
 * vacuous-citation shape in a comment instead of a counter.
 *
 * REVERSIBLE, and here is the reversal. A delegated ruling is a normal change
 * the owner may overrule at any time. If BR-ADM-01-13 is restored as written,
 * the repair is to drop the per-group batching and commit each control's
 * change on interaction; `BR-ADM-06-03`'s unsaved-changes warning then has
 * nothing to warn about on THIS screen and its Roles-grid sibling stays, since
 * that grid is a set of switches saved together by its own rule rather than by
 * this instruction. The sibling deviation lives at
 * `apps/web/src/features/admin/roles-page.tsx` and is covered by the same
 * ruling.
 *
 * The search matches the ENGLISH AND ARABIC name and hint at once, whichever
 * language the screen is in (BR-ADM-01-10 / NFR-05a) — both dictionaries are
 * loaded, so the lookup is local. NFR-05a's "every search box in the product
 * should do the same" is a should; this box, the one the rule describes, does.
 *
 * OQ-15-11 — "The **Viewing as** control on ADM-01 lets somebody see the
 * screen through another role's eyes. Does it ship to customers?" — "Yes, but
 * only for the Owner and the Administrator role, and clearly labelled as a
 * preview. Add **no role** as a fourth thing to preview." Built under
 * delegated ruling D38 (27 Aug 2026); PART 15's acceptance checklist asks for
 * that decision to be RECORDED, and this is where it is recorded in code. The
 * model, and the spec sentence that makes it a preview rather than an
 * impersonation, are in `viewing-as.ts`.
 *
 * NFR-01a — the prototype carried the settings names and hints INLINE with the
 * settings and the rule is that a build "must not lose them": here every row's
 * name and hint are `settings.label.<key>` / `settings.hint.<key>` dictionary
 * entries, and `i18n-resources.test.ts` (NFR-01b) fails if either language
 * drops one.
 */

function searchableText(key: string): string {
  const parts: string[] = [];
  for (const lng of ['en', 'ar'] as const) {
    for (const kind of ['label', 'hint'] as const) {
      const value = i18n.getResource(lng, 'translation', `settings.${kind}.${key}`) as
        | string
        | undefined;
      if (value) parts.push(value.toLowerCase());
    }
  }
  return parts.join(' ');
}

function scopeBadgeClass(scope: SettingRow['scope']): string {
  switch (scope) {
    case 'personal':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300';
    case 'role':
      return 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300';
    case 'owner':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

export function SettingsPage() {
  const { t } = useTranslation();
  const { data: ownRows, isPending, error } = useSettings();
  const updateSetting = useUpdateSetting();
  const lockSetting = useLockSetting();
  const overrideSetting = useOverrideSetting();

  /*
   * ── OQ-15-11 / D38 — "Viewing as" ────────────────────────────────────────
   *
   * The control is the Owner's and the Administrator's alone: OQ-15-11's own
   * clause ("only for the Owner and the Administrator role") and §6's own
   * "Should be hidden from anybody else". An Accountant holding `admin.view`
   * opens this screen and must not find it — so this is an ABSENCE, not a
   * greyed control (BR-PERM-19; and §6 says hidden, which overrules 16.7's
   * default shape the way ADM-06 §11.2's Administrator option does).
   *
   * `admin.edit` is how this build spells "the Administrator role" on this
   * screen — `SettingsService` gates every company-value write with
   * `isOwner || holds('admin.edit')` under BR-ADM-01-04. Asking the same
   * question here rather than a second one of my own is what keeps the
   * control's audience and the screen's authority from drifting apart.
   *
   * IT LIVES IN REACT STATE AND NOWHERE ELSE. Not localStorage, not the URL,
   * not a cookie: a preview that survives a reload is a person who has
   * forgotten they are in one, and the banner is the only thing standing
   * between them and a wrong conclusion about their own permissions.
   */
  const caps = useCapabilities();
  const mayPreview = caps.isOwner || holds(caps, 'admin.edit');
  const [viewAs, setViewAs] = useState<ViewAsTarget>(VIEW_AS_OFF);
  // Only "a chosen role" needs the grid; Owner, Administrator and "no role"
  // are constants (see viewing-as.ts). A grid that fails to load costs the
  // role options and nothing else.
  const { data: securityRoles } = useSecurityRoles({ enabled: mayPreview });
  const previewing = mayPreview && viewAs.kind !== 'self';
  const authority = useMemo(
    () => previewAuthority(viewAs, securityRoles ?? []),
    [viewAs, securityRoles],
  );
  const previewedRole =
    viewAs.kind === 'role'
      ? (securityRoles?.find((role) => role.id === viewAs.roleId)?.name ??
        t('settings.viewAs.unknownRole'))
      : viewAs.kind === 'self'
        ? ''
        : t(`settings.viewAs.${viewAs.kind}`);

  /**
   * The rows the screen draws. With the preview off these are the caller's
   * own, untouched — the projection runs ONLY while previewing, so the
   * ordinary screen is what it was before D38, expression for expression.
   */
  const rows = useMemo(
    () => (previewing && ownRows ? previewRows(ownRows, authority) : ownRows),
    [ownRows, previewing, authority],
  );

  const [group, setGroup] = useState<SettingGroup>('fiscal');
  const [query, setQuery] = useState('');
  // MD-09 §9: with the Payment terms setting off, the screen is not offered.
  const paymentTermsOn = useOrgSetting<boolean>('invoicing.payment_terms');
  /** Unsaved edits, key → value. Saved per group (the Phase-4 batch). */
  const [draft, setDraft] = useState<Record<string, SettingValue>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedGroup, setSavedGroup] = useState<SettingGroup | null>(null);

  const arabic = i18n.language.startsWith('ar');
  /*
   * A21 / A22 — the chart, for the rows whose value IS an account code.
   *
   * Fetched unconditionally rather than per row: `useAccounts` is one cached
   * query with a 60s stale time, and this screen always draws at least the
   * twelve stock_accounts rows plus these two. A 403 (this screen wants
   * `admin.view`, the chart wants `accounting.view`, and neither implies the
   * other) leaves `data` undefined, which falls the control back to the text
   * box it has always been rather than to an empty picker.
   */
  const { data: chart } = useAccounts();
  const accountOptions = useMemo(
    // The journal editor's own filter, and the same two rules behind it:
    // BR-FIN-02-04 (a line must name a postable account) and BR-FIN-01-09 (an
    // inactive account is a posting target nowhere).
    () => (chart ?? []).filter((account) => account.postable && account.active),
    [chart],
  );

  useEffect(() => {
    document.title = pageTitle(t('settings.title'));
  }, [t]);

  const searching = query.trim().length > 0;
  const visible = useMemo(() => {
    if (!rows) return [];
    if (!searching) return rows.filter((row) => row.group === group);
    const q = query.trim().toLowerCase();
    // BR-ADM-01-10, BR-ADM-01-11: EN + AR name and hint, across every group.
    return rows.filter((row) => searchableText(row.key).includes(q));
  }, [rows, group, query, searching]);

  const groupCounts = useMemo(() => {
    const counts = new Map<SettingGroup, number>();
    for (const row of rows ?? []) counts.set(row.group, (counts.get(row.group) ?? 0) + 1);
    return counts;
  }, [rows]);

  const dirtyInGroup = useMemo(() => {
    if (!rows) return [];
    return Object.keys(draft).filter((key) => {
      const row = rows.find((entry) => entry.key === key);
      if (!row) return false;
      const shown = searching ? true : row.group === group;
      const current = row.scope === 'personal' ? row.effectiveValue : row.value;
      return shown && draft[key] !== current;
    });
  }, [draft, rows, group, searching]);

  const setValue = (key: string, value: SettingValue) => {
    setSavedGroup(null);
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const saveGroup = async () => {
    if (!rows) return;
    setSaveError(null);
    try {
      for (const key of dirtyInGroup) {
        const row = rows.find((entry) => entry.key === key)!;
        const raw = draft[key]!;
        const value = row.type === 'number' ? Number(raw) : raw;
        // Personal rows are the caller's own (BR-ADM-01-03) — saved through
        // the override route; company rows through the org route.
        if (row.scope === 'personal') {
          await overrideSetting.mutateAsync({ key, value });
        } else {
          await updateSetting.mutateAsync({ key, value });
        }
      }
      setDraft((prev) => {
        const next = { ...prev };
        for (const key of dirtyInGroup) delete next[key];
        return next;
      });
      setSavedGroup(group);
    } catch (cause) {
      setSaveError(
        localizeServerMessage(t, cause instanceof Error ? cause.message : String(cause)),
      );
    }
  };

  const toggleLock = async (row: SettingRow) => {
    setSaveError(null);
    try {
      await lockSetting.mutateAsync({ key: row.key, locked: !row.locked });
    } catch (cause) {
      setSaveError(
        localizeServerMessage(t, cause instanceof Error ? cause.message : String(cause)),
      );
    }
  };

  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {localizeServerMessage(t, error.message)}
      </p>
    );
  }

  return (
    <div className="grid gap-4">
      {/* ADM-04 §3, the SECOND door: "Main menu › General Settings ›
          Notifications — which needs `admin` View, like the rest of that menu
          group." Both doors reach the same screen; the bell needs no
          permission and this one is only visible to somebody already standing
          inside the Settings group. */}
      <nav className="flex flex-wrap gap-1 border-b pb-px" aria-label={t('shell.navSettings')}>
        <span className="rounded-t-md border-b-2 border-primary px-3 py-2 text-sm font-medium">
          {t('settings.tabGeneral')}
        </span>
        <Link
          to="/notifications"
          className="rounded-t-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          {t('notif.title')}
        </Link>
        {/* ADM-03 §3 — the Import / Export Centre's only door. */}
        <Link
          to="/settings/import-export"
          className="rounded-t-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          {t('impexp.title')}
        </Link>
        {/* ADM-02 §3 — Integrations & API Keys. */}
        <Link
          to="/settings/integrations"
          className="rounded-t-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          {t('integrations.title')}
        </Link>
        {/* MD-13 §3 — "There is no other way in. This screen is deliberately
            hard to reach." One tab, in the group the spec names, and nowhere
            else in the product links to it. */}
        <Link
          to="/settings/number-sequences"
          className="rounded-t-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          {t('numseq.title')}
        </Link>
        {/* MD-09 §3 — "Main menu › General Settings › Payment terms"; §9 —
            with the org switch off, the screen is not offered at all. */}
        {paymentTermsOn ? (
          <Link
            to="/settings/payment-terms"
            className="rounded-t-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            {t('payterms.title')}
          </Link>
        ) : null}
        {/* INV-H6 / UB-03 — Printing: the `print.*` rows with a live preview.
            The same rows are in this screen's rail too; the tab is the door
            the top bar's Print options button also opens. */}
        <Link
          to="/settings/printing"
          className="rounded-t-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          {t('printset.title')}
        </Link>
      </nav>

      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('settings.title')}</h2>
        <div className="relative">
          <Search aria-hidden className="absolute start-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            className="ps-8"
            placeholder={t('settings.searchPh')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label={t('settings.searchPh')}
          />
        </div>
        {/*
         * ADM-01 §4's layout puts it here — "A search box and a 'Viewing as'
         * control across the top" — and §6 puts it "Top right". The four
         * choices are §6's own four as amended by R18 and confirmed by
         * OQ-15-11: Owner, Admin, a chosen role, and no role. "Yourself" is
         * the off state and not a fifth thing to preview.
         */}
        {mayPreview ? (
          <div className="flex items-center gap-2">
            <Eye aria-hidden className="size-4 text-muted-foreground" />
            <NativeSelect
              className="h-9 w-56 py-0 text-sm"
              aria-label={t('settings.viewAs.label')}
              value={encodeViewAs(viewAs)}
              onChange={(event) => setViewAs(decodeViewAs(event.target.value))}
            >
              <option value="self">{t('settings.viewAs.self')}</option>
              <option value="owner">{t('settings.viewAs.owner')}</option>
              <option value="admin">{t('settings.viewAs.admin')}</option>
              <option value="noRole">{t('settings.viewAs.noRole')}</option>
              {securityRoles && securityRoles.length > 0 ? (
                <optgroup label={t('settings.viewAs.roles')}>
                  {securityRoles.map((role) => (
                    <option key={role.id} value={`role:${role.id}`}>
                      {role.name}
                    </option>
                  ))}
                </optgroup>
              ) : null}
            </NativeSelect>
          </div>
        ) : null}
      </div>

      {/*
       * THE PREVIEW BANNER — "clearly labelled as a preview" is OQ-15-11's own
       * requirement, and §6 calls the control "a demonstration control, not a
       * real permission switch". So this is not a hint: it names the role being
       * previewed, says the caller's own permissions still decide, and carries
       * the way out. `role="status"` so a screen-reader user learns the screen
       * changed identity rather than silently reading a shorter rail.
       *
       * Amber, not blue: it sits directly above the ordinary blue information
       * banner, and two same-coloured notices read as one paragraph. The
       * distinction has to survive being glanced at.
       *
       * THE PREVIEWED ROLE IS ITS OWN NODE, NOT AN ICU ARGUMENT IN THE
       * SENTENCE, and that is a decision rather than a shortcut. A role name
       * is tenant data — somebody's custom role can be called anything, in
       * either script — so interpolating it into a translated frame puts
       * arbitrary text next to ICU's braces, which is the one place an
       * apostrophe changes meaning (the `''{workstation}''` defect). A chip
       * beside the sentence also reads louder than a name buried in it, and
       * the flex gap keeps the order right in Arabic without a single
       * directional character.
       */}
      {previewing ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-md border-2 border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-600 dark:bg-amber-950 dark:text-amber-200"
        >
          <Eye aria-hidden className="size-4 shrink-0" />
          <div className="me-auto flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="font-semibold">{t('settings.viewAs.label')}</span>
            <span className="rounded bg-amber-200 px-2 py-0.5 font-bold dark:bg-amber-800">
              {previewedRole}
            </span>
            <span>{t('settings.viewAs.banner')}</span>
          </div>
          <Button
            variant="outline"
            className="h-8 border-amber-500 px-3 text-xs"
            onClick={() => setViewAs(VIEW_AS_OFF)}
          >
            {t('settings.viewAs.exit')}
          </Button>
        </div>
      ) : null}

      <p className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200">
        {t('settings.banner')}
      </p>

      {/*
       * "The hardest one to picture" (§6, of the no-role choice). A role with
       * no `admin.view`, and a person with no role at all, do not see a
       * shorter rail — ADM-01 §2 says they "cannot open this screen at all".
       * BR-PERM-19/BR-PERM-43 make that an ABSENCE, so the preview shows the
       * closed door rather than a stripped screen, which would misinform the
       * Owner in exactly the direction that matters.
       */}
      {previewing && !authority.canOpen ? (
        <Card>
          <CardContent className="grid justify-items-center gap-2 py-8 text-center">
            <span className="rounded bg-muted px-2 py-0.5 text-sm font-bold">{previewedRole}</span>
            <p className="text-sm font-medium">{t('settings.viewAs.cannotOpen')}</p>
            <p className="text-xs text-muted-foreground">
              {t('settings.viewAs.personalElsewhere')}
            </p>
          </CardContent>
        </Card>
      ) : (
      <div className="grid gap-4 lg:grid-cols-[246px_1fr]">
        {/* The group rail — a group with nothing visible is DROPPED (BR-ADM-01-06). */}
        <nav className={searching ? 'opacity-50' : ''} aria-label={t('settings.title')}>
          <ul className="grid gap-1">
            {SETTING_GROUPS.filter((entry) => (groupCounts.get(entry) ?? 0) > 0).map((entry) => (
              <li key={entry}>
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    setGroup(entry);
                  }}
                  className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm ${
                    !searching && entry === group
                      ? 'bg-primary/10 font-semibold text-foreground'
                      : 'hover:bg-muted'
                  }`}
                >
                  <span>{t(`settings.group.${entry}`)}</span>
                  <span className="text-xs text-muted-foreground">{groupCounts.get(entry)}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="grid gap-3 self-start">
          {isPending ? <p className="text-sm text-muted-foreground">{t('common.loading')}</p> : null}
          {/* ERR-ADM-01-01 — ADM-01 §14: "Searched for something with no match
              | 'No settings match your search.' | Search for something else".
              The spec's own sentence, keyed so it exists in both locales.
              Guarded on `!isPending` so a slow first load shows "loading"
              rather than claiming the search found nothing. */}
          {!isPending && visible.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('settings.noMatch')}</p>
          ) : null}
          {saveError ? (
            <p role="alert" className="text-sm text-destructive">
              {saveError}
            </p>
          ) : null}

          {visible.map((row) => {
            const current =
              draft[row.key] ?? (row.scope === 'personal' ? row.effectiveValue : row.value);
            const editable = row.canChange || row.scope === 'personal';
            return (
              <Card key={row.key} className={editable ? '' : 'opacity-60'}>
                <CardContent className="flex flex-wrap items-center gap-3 py-3">
                  <div className="me-auto grid gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{t(`settings.label.${row.key}`)}</span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${scopeBadgeClass(row.scope)}`}
                      >
                        {t(`settings.scope.${row.scope}`)}
                      </span>
                      {row.overridden ? (
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-800 dark:bg-blue-950 dark:text-blue-300">
                          {t('settings.yourOverride')}
                        </span>
                      ) : null}
                      {row.scope !== 'personal' && row.scope !== 'owner' && row.canChange ? (
                        <button
                          type="button"
                          onClick={() => void toggleLock(row)}
                          aria-label={row.locked ? t('settings.lockedTip') : t('settings.openTip')}
                          title={row.locked ? t('settings.lockedTip') : t('settings.openTip')}
                          className={row.locked ? 'text-amber-700 dark:text-amber-400' : 'text-green-700 dark:text-green-400'}
                        >
                          {row.locked ? (
                            <Lock aria-hidden className="size-4" />
                          ) : (
                            <LockOpen aria-hidden className="size-4" />
                          )}
                        </button>
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground">{t(`settings.hint.${row.key}`)}</p>
                  </div>

                  {row.type === 'switch' ? (
                    <Checkbox
                      aria-label={t(`settings.label.${row.key}`)}
                      checked={Boolean(current)}
                      disabled={!editable}
                      onCheckedChange={(checked) => setValue(row.key, checked === true)}
                    />
                  ) : accountMode(row.key) !== null ? (
                    /*
                     * A21 / A22 — a setting whose VALUE IS AN ACCOUNT CODE
                     * gets the chart itself rather than a free-text box.
                     *
                     * The server already refuses a code that names no
                     * postable, active account (SETTINGS_MESSAGES.notAnAccount
                     * — the twelve stock_accounts rows have been refusing it
                     * since INV-08 §12's settings table was built), so this is
                     * not the enforcement; it is the difference between
                     * meeting that refusal and never being able to cause it.
                     * The list is filtered the way every account picker in the
                     * product is filtered — postable and active only, the
                     * journal editor's own idiom (BR-FIN-02-04 /
                     * BR-FIN-01-09).
                     *
                     * IT FALLS BACK TO THE TEXT BOX WHEN THE CHART IS NOT
                     * READABLE, and that is not defensive dressing: this
                     * screen needs `admin.view`, `GET /api/accounts` needs
                     * `accounting.view`, and nothing guarantees one implies
                     * the other. An administrator without the accounting
                     * grant would otherwise meet an empty picker with no way
                     * to type the code they already know — a control that
                     * silently removes an ability the text box had.
                     */
                    accountOptions.length > 0 ? (
                      <NativeSelect
                        aria-label={t(`settings.label.${row.key}`)}
                        className="w-52"
                        value={String(current)}
                        disabled={!editable}
                        onChange={(event) => setValue(row.key, event.target.value)}
                      >
                        {/*
                         * Only where a blank MEANS something. On
                         * `treasury.deposit_account` an empty value is the
                         * ruling's own default — "the first postable bank
                         * leaf", worked out against the tenant's chart at
                         * posting time — so it has to be choosable. On a
                         * 'required' row a blank is a typo and is refused.
                         */}
                        {accountMode(row.key) === 'optional' ? (
                          <option value="">
                            {/* Wave 5D: the returns-account blank means 5050,
                                not the first cash account. */}
                            {t(
                              row.key === 'purchasing.returns_account'
                                ? 'settings.accountUseVariance'
                                : // Wave 4D: a blank cash-invoice default means none.
                                  row.key === 'invoicing.default_cash_account'
                                  ? 'settings.accountNoDefault'
                                  : 'settings.accountUseDefault',
                            )}
                          </option>
                        ) : null}
                        {accountOptions.map((account) => (
                          <option key={account.id} value={account.code}>
                            {account.code} {arabic ? account.nameAr : account.name}
                          </option>
                        ))}
                      </NativeSelect>
                    ) : (
                      <Input
                        aria-label={t(`settings.label.${row.key}`)}
                        type="text"
                        className="w-40"
                        dir="ltr"
                        value={String(current)}
                        disabled={!editable}
                        onChange={(event) => setValue(row.key, event.target.value)}
                      />
                    )
                  ) : row.type === 'enum' ? (
                    <NativeSelect
                      aria-label={t(`settings.label.${row.key}`)}
                      className="w-52"
                      value={String(current)}
                      disabled={!editable}
                      onChange={(event) => setValue(row.key, event.target.value)}
                    >
                      {(row.options ?? []).map((option) => (
                        <option key={option} value={option}>
                          {t(`settings.opt.${row.key}.${option}`)}
                        </option>
                      ))}
                    </NativeSelect>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Input
                        aria-label={t(`settings.label.${row.key}`)}
                        type={row.type === 'date' ? 'date' : 'text'}
                        inputMode={row.type === 'number' ? 'numeric' : undefined}
                        className="w-40"
                        dir="ltr"
                        value={String(current)}
                        disabled={!editable}
                        onChange={(event) => setValue(row.key, event.target.value)}
                      />
                      {row.type === 'percent' ? (
                        <span className="text-sm text-muted-foreground">{t('unit.percentSign')}</span>
                      ) : null}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {dirtyInGroup.length > 0 ? (
            <div className="flex items-center gap-3">
              <Button onClick={() => void saveGroup()} disabled={updateSetting.isPending}>
                {t('settings.saveGroup')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setDraft({});
                  setSaveError(null);
                }}
              >
                {t('common.cancel')}
              </Button>
              <span className="text-xs text-muted-foreground">
                {t('settings.unsaved', { count: dirtyInGroup.length })}
              </span>
            </div>
          ) : savedGroup === group && !searching ? (
            <p className="text-sm text-green-700 dark:text-green-400">{t('settings.saved')}</p>
          ) : null}

        </div>
      </div>
      )}
    </div>
  );
}
